import {
  type F2fTransactionStatus,
  type F2fTransactionStatusCode,
  Query,
} from "@gen/app-query";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { useParams } from "@tanstack/react-router";
import { useMemo } from "react";

import { LoadingScreen } from "~/components/loading_screen";
import { PublicSvgIcon } from "~/components/public_svg_icon";
import {
  SectionSideNav,
  type SectionSideNavItem,
} from "~/components/section_side_nav";
import { StatusChip } from "~/components/status_chip";
import { TransactionMoney } from "~/entities/transaction";

import { BuyerLedger } from "./BuyerLedger";
import { ClientSection } from "./ClientSection";
import { ExtrasSection } from "./ExtrasSection";
import { ItemsSection } from "./ItemsSection";
import { PaymentReviewBanner } from "./PaymentReviewBanner";
import { SellerLedger } from "./SellerLedger";
import { TransactionTimelineSection } from "./TransactionTimeline";
import { UserDetails } from "./UserDetails";

const isF2fStatusCode = (
  status: string | undefined,
  statuses?: F2fTransactionStatus[],
): status is F2fTransactionStatusCode =>
  Boolean(status && statuses?.some((s) => s.code === status));

export function F2fTransaction() {
  const { clientId, transactionId } = useParams({
    from: "/clients/$clientId/f2f/transactions/$transactionId",
    select: (params) => ({
      clientId: params.clientId,
      transactionId: Number(params.transactionId),
    }),
    shouldThrow: true,
  });

  const { data: f2fTransactionData, isFetching: isFetchingF2fTransaction } =
    Query.useCore_getClientF2fTransactionQuery(clientId, transactionId, {
      enabled: !!clientId && transactionId !== undefined,
    });

  const { data: chargeRatesData } =
    Query.useCore_getClientF2fTransactionChargeRatesQuery(
      clientId,
      transactionId,
      {
        enabled: !!clientId && transactionId !== undefined,
      },
    );

  const { data: clientData } = Query.useCore_getClientQuery(clientId);

  const { data: statusData } = Query.useCore_getTransactionStatusesQuery();

  const { data: metadata } = Query.useCore_getTransactionMetadataQuery({
    client_id: clientId,
    transaction_id: transactionId,
  });

  const statusMap = new Map<string, F2fTransactionStatus>(
    (statusData?.f2f ?? []).map((s: F2fTransactionStatus) => [s.code, s]),
  );

  const transactionMoney = f2fTransactionData
    ? new TransactionMoney(f2fTransactionData)
    : null;

  const isDepositInReview =
    !!f2fTransactionData?.deposit_review_flagged &&
    !f2fTransactionData?.deposit_review?.finished;

  const isRemainderInReview =
    !!f2fTransactionData?.remainder_review_flagged &&
    !f2fTransactionData?.remainder_review?.finished;

  const txItems = f2fTransactionData?.f2f_items ?? [];
  const hasItems = txItems.length > 0;

  const sideNavItems = useMemo<SectionSideNavItem[]>(() => {
    const items = [{ id: "summary", label: "Transaction Details" }];
    if (isDepositInReview || isRemainderInReview) {
      items.push({ id: "payment-review", label: "Under Review" });
    }
    if (hasItems) {
      items.push({ id: "items", label: "Items" });
    }
    items.push(
      { id: "buyer", label: "Buyer Overview" },
      { id: "seller", label: "Seller Overview" },
      { id: "timeline", label: "Timeline" },
      { id: "client", label: "Client" },
      { id: "extras", label: "Extras" },
    );
    return items;
  }, [isDepositInReview, isRemainderInReview, hasItems]);

  const theme = useTheme();

  if (isFetchingF2fTransaction) {
    return <LoadingScreen />;
  }

  if (!f2fTransactionData || !transactionId) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ p: 4 }}>
        <Typography variant="body1">Transaction not found.</Typography>
      </Stack>
    );
  }

  const statusKey = isF2fStatusCode(f2fTransactionData.status, statusData?.f2f)
    ? f2fTransactionData.status
    : undefined;
  const statusInfo = statusKey ? statusMap.get(statusKey) : undefined;

  const f2fTransactionDataForExtras = chargeRatesData
    ? {
        ...f2fTransactionData,
        charge_rates: (({ transaction_id: _id, ...rest }) => rest)(
          chargeRatesData,
        ),
      }
    : f2fTransactionData;

  return (
    <Box
      sx={{
        display: { xs: "block", md: "flex" },
        gap: { md: 4.5 },
        alignItems: "flex-start",
      }}
    >
      <Box
        sx={{
          display: { xs: "contents", md: "block" },
          order: { md: 1 },
          flex: { md: "0 0 200px" },
          minWidth: 0,
          alignSelf: "stretch",
        }}
      >
        <SectionSideNav items={sideNavItems} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack
          id="summary"
          flexDirection={{ xs: "column", lg: "row" }}
          spacing={{ xs: 2, lg: 5 }}
          sx={{
            mb: { xs: 3, lg: 5 },
            p: { xs: 2, sm: 3, lg: 6 },
            backgroundColor: "primary._states.hover",
            borderRadius: 2,
            scrollMarginTop: theme.spacing(10),
          }}
          useFlexGap
        >
          <Stack flexDirection="column" flexGrow={1}>
            <Typography variant="body1DashedDivider">Client</Typography>
            <Typography variant="body2" data-testid="transaction-client-text">
              {(clientData?.name ?? f2fTransactionData.client_id) || "N/A"}
            </Typography>
          </Stack>
          <Stack flexDirection="column" flexGrow={1}>
            <Typography variant="body1DashedDivider">
              Transaction total
            </Typography>
            <Typography variant="body2" data-testid="transaction-total-text">
              {transactionMoney?.getTotal().format()}
            </Typography>
          </Stack>
          <Stack flexDirection="column" flexGrow={1}>
            <Typography variant="body1DashedDivider">Description</Typography>
            <Typography
              variant="body2"
              data-testid="transaction-description-text"
            >
              {f2fTransactionData.description || "N/A"}
            </Typography>
          </Stack>
          <Stack flexDirection="column" flexGrow={1}>
            <Typography variant="body1DashedDivider">Status</Typography>
            {statusInfo ? (
              <StatusChip
                label={statusInfo.label}
                color={statusInfo.color}
                dataTestId="transaction-status-text"
              />
            ) : (
              <Chip
                label={f2fTransactionData.status}
                data-testid="transaction-status-text"
              />
            )}
          </Stack>
        </Stack>

        {(isDepositInReview || isRemainderInReview) && (
          <Box id="payment-review" sx={{ scrollMarginTop: theme.spacing(10) }}>
            <PaymentReviewBanner
              isDepositInReview={isDepositInReview}
              isRemainderInReview={isRemainderInReview}
            />
          </Box>
        )}
        {hasItems && (
          <Box
            id="items"
            sx={{
              mb: { xs: 3, lg: 5 },
              scrollMarginTop: theme.spacing(10),
            }}
          >
            <ItemsSection items={txItems} />
          </Box>
        )}
        <Grid size={{ xs: 12, lg: 1014 }}>
          <Grid
            id="buyer"
            container
            spacing={{ xs: 3, lg: 6 }}
            sx={{
              mb: { xs: 3, lg: 5 },
              backgroundColor: "primary._states.hover",
              borderRadius: 2,
              height: "max-content",
              p: { xs: 2, lg: 0 },
              scrollMarginTop: theme.spacing(10),
            }}
          >
            <Grid
              sx={{
                pl: { xs: 0, lg: 6 },
                pt: { xs: 0, lg: 6 },
                py: { xs: 0, lg: 6 },
              }}
              size={{ xs: 12, lg: 6 }}
            >
              <Stack spacing={{ xs: 3, lg: 5 }}>
                <Typography
                  variant="h5SolidDivider"
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <PublicSvgIcon name="shopping_cart" /> Buyer Overview
                </Typography>
                <UserDetails
                  transactionData={f2fTransactionData}
                  clientId={clientId}
                  userTransactionRole="buyer"
                />
                <BuyerLedger transactionData={f2fTransactionData} />
              </Stack>
            </Grid>

            <Grid
              id="seller"
              sx={{
                pr: { xs: 0, lg: 6 },
                pt: { xs: 0, lg: 6 },
                py: { xs: 0, lg: 6 },
                scrollMarginTop: theme.spacing(10),
              }}
              size={{ xs: 12, lg: 6 }}
            >
              <Stack spacing={{ xs: 3, lg: 5 }}>
                <Typography
                  variant="h5SolidDivider"
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <PublicSvgIcon name="storefront" /> Seller Overview
                </Typography>
                <UserDetails
                  transactionData={f2fTransactionData}
                  clientId={clientId}
                  userTransactionRole="seller"
                />
                <SellerLedger
                  transactionData={f2fTransactionData}
                  allocateExtraPriceToClient={
                    f2fTransactionData?.allocate_extra_price_to_client ??
                    clientData?.allocate_extra_price_to_client
                  }
                  allocatePostagePriceTo={
                    f2fTransactionData?.allocate_postage_price_to ??
                    clientData?.allocate_postage_price_to
                  }
                />
              </Stack>
            </Grid>
          </Grid>
        </Grid>
        <Grid size={{ xs: 12, lg: 474 }}>
          <Stack spacing={{ xs: 3, lg: 0 }}>
            <Box id="timeline" sx={{ scrollMarginTop: theme.spacing(10) }}>
              <TransactionTimelineSection
                timeline={f2fTransactionData.timeline || []}
                statusData={statusData}
              />
            </Box>
            <Box id="client" sx={{ scrollMarginTop: theme.spacing(10) }}>
              <ClientSection transactionData={f2fTransactionData} />
            </Box>
            <Box id="extras" sx={{ scrollMarginTop: theme.spacing(10) }}>
              <ExtrasSection
                transactionData={f2fTransactionDataForExtras}
                transactionId={transactionId}
                metadata={metadata}
              />
            </Box>
          </Stack>
        </Grid>
      </Box>
    </Box>
  );
}
